import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit, CheckCircle, AlertCircle, Loader2,
  RefreshCw, Globe, Shield, Eye, EyeOff, Upload, X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { apiClient } from '@/utils/apiClient';
import { toast } from '@/stores/toastStore';

interface Cluster {
  id: number;
  name: string;
  base_url: string;
  cluster_api_token: string;
  environment_api_token: string;
  is_active: boolean;
  is_base: boolean;
  verify_ssl: boolean;
  ca_bundle_path: string | null;
  health_status: string;
  health_message: string | null;
  last_health_check: string | null;
}

export const ClusterSettings: React.FC = () => {
  const { t } = useTranslation();
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [multiClusterEnabled, setMultiClusterEnabled] = useState(true);
  const [clusterBound, setClusterBound] = useState<string | null>(null);
  const [routingMode, setRoutingMode] = useState('sequential');
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [showTokens, setShowTokens] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    base_url: '',
    cluster_api_token: '',
    environment_api_token: '',
    verify_ssl: true,
  });

  const fetchClusters = useCallback(async () => {
    try {
      const data = await apiClient.listClusters();
      setClusters(data.clusters || []);
      setMultiClusterEnabled(data.multi_cluster_enabled ?? true);
      setClusterBound(data.cluster_bound || null);
    } catch (e) {
      console.error('Failed to load clusters', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRouting = useCallback(async () => {
    try {
      const data = await apiClient.getClusterRoutingConfig();
      setRoutingMode(data.routing?.mode || 'sequential');
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchClusters();
    fetchRouting();
  }, [fetchClusters, fetchRouting]);

  const resetForm = () => {
    setFormData({ name: '', base_url: '', cluster_api_token: '', environment_api_token: '', verify_ssl: true });
    setIsAdding(false);
    setEditingId(null);
    setShowTokens(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await apiClient.updateCluster(editingId, formData);
        toast.success(`Cluster "${formData.name}" updated`);
      } else {
        await apiClient.addCluster(formData);
        toast.success(`Cluster "${formData.name}" added`);
      }
      resetForm();
      fetchClusters();
    } catch (err: any) {
      toast.error(err?.message || (editingId ? 'Failed to update cluster' : 'Failed to add cluster'));
    }
  };

  const handleEditCluster = async (cluster: Cluster) => {
    try {
      // Fetch unmasked tokens for editing
      const data = await apiClient.getCluster(cluster.id, true);
      const full = data.cluster;
      setFormData({
        name: full.name,
        base_url: full.base_url,
        cluster_api_token: full.cluster_api_token,
        environment_api_token: full.environment_api_token,
        verify_ssl: full.verify_ssl ?? true,
      });
      setEditingId(cluster.id);
      setShowTokens(false);
      setIsAdding(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load cluster details');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete cluster "${name}"?`)) return;
    try {
      await apiClient.deleteCluster(id);
      toast.success(`Cluster "${name}" deleted`);
      fetchClusters();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete cluster');
    }
  };

  const translateHealthMessage = (msg: string | null | undefined): string => {
    if (!msg) return '';
    const map: Record<string, string> = {
      'API reachable': t.settings.healthApiReachable,
      'Authentication failed (check tokens)': t.settings.healthAuthFailed,
      'Connection refused': t.settings.healthConnectionRefused,
      'Connection timed out': t.settings.healthTimedOut,
    };
    return map[msg] || msg;
  };

  const translateHealthStatus = (status: string): string => {
    switch (status) {
      case 'healthy':
        return t.settings.healthHealthy;
      case 'degraded':
        return t.settings.healthDegraded;
      case 'unreachable':
        return t.settings.healthUnreachable;
      default:
        return t.settings.healthUnknown;
    }
  };

  const routingLabel = (mode: string): string =>
    mode === 'parallel' ? t.settings.routingParallel : t.settings.routingSequential;

  const handleTest = async (id: number) => {
    setTestingId(id);
    try {
      const result = await apiClient.testCluster(id);
      const hs = result.health_status || 'unknown';
      const summary = `${translateHealthStatus(hs)} — ${translateHealthMessage(result.message)}`;
      if (hs === 'healthy') {
        toast.success(summary);
      } else {
        toast.error(summary);
      }
      fetchClusters();
    } catch (err: any) {
      toast.error(err?.message || 'Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleToggleActive = async (cluster: Cluster) => {
    try {
      await apiClient.updateCluster(cluster.id, { is_active: !cluster.is_active });
      fetchClusters();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update cluster');
    }
  };

  const handleRoutingChange = async (mode: string) => {
    try {
      await apiClient.updateClusterRoutingConfig(mode);
      setRoutingMode(mode);
      toast.success(`${t.settings.queryRouting}: ${routingLabel(mode)}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update routing');
    }
  };

  const healthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'unreachable':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Globe className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {(t as any).settings?.clusters || 'Clusters'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {(t as any).settings?.clustersDescription || 'Manage Astromkey cluster connections'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { fetchClusters(); fetchRouting(); }}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {multiClusterEnabled && !isAdding && (
            <button
              onClick={() => setIsAdding(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <Plus className="mr-2 h-4 w-4" />
              {(t as any).settings?.addCluster || 'Add Cluster'}
            </button>
          )}
        </div>
      </div>

      {/* Cluster-bound notice */}
      {!multiClusterEnabled && clusterBound && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-amber-500" />
            <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {(t as any).settings?.clusterBoundNotice || 'Multi-cluster disabled — license is cluster-bound'}
            </span>
          </div>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-300 ml-7">
            {clusterBound}
          </p>
        </div>
      )}

      {/* Routing mode (only when multi-cluster is enabled and >1 cluster) */}
      {multiClusterEnabled && clusters.length > 1 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            {(t as any).settings?.queryRouting || 'Query Routing'}
          </h4>
          <div className="flex space-x-4">
            {['sequential', 'parallel'].map((mode) => (
              <label key={mode} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  value={mode}
                  checked={routingMode === mode}
                  onChange={() => handleRoutingChange(mode)}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{routingLabel(mode)}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Add form */}
      {isAdding && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
            {editingId
              ? ((t as any).settings?.editCluster || 'Edit Cluster')
              : ((t as any).settings?.addNewCluster || 'Add New Cluster')}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {(t as any).settings?.clusterName || 'Cluster Name'}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="production"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {(t as any).settings?.clusterUrl || 'Cluster URL'}
                </label>
                <input
                  type="url"
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="https://cluster.astromkey.ru/e/env-id"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cluster API Token
                </label>
                <div className="relative">
                  <input
                    type={showTokens ? 'text' : 'password'}
                    value={formData.cluster_api_token}
                    onChange={(e) => setFormData({ ...formData, cluster_api_token: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="dt0c01.****"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowTokens(!showTokens)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showTokens ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Environment API Token
                </label>
                <div className="relative">
                  <input
                    type={showTokens ? 'text' : 'password'}
                    value={formData.environment_api_token}
                    onChange={(e) => setFormData({ ...formData, environment_api_token: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="dt0c01.****"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowTokens(!showTokens)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showTokens ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3 mt-1">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.verify_ssl}
                  onChange={(e) => setFormData({ ...formData, verify_ssl: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {(t as any).settings?.verifySSL || 'Verify SSL certificate'}
                </span>
              </label>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {(t as any).settings?.verifySSLHint || 'Disable for self-signed certificates'}
              </span>
            </div>
            {editingId && formData.verify_ssl && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {(t as any).settings?.caBundle || 'CA Certificate (PEM)'}
                </label>
                {(() => {
                  const cluster = clusters.find(c => c.id === editingId);
                  const hasBundle = cluster?.ca_bundle_path;
                  return hasBundle ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-green-600 dark:text-green-400 flex items-center space-x-1">
                        <CheckCircle className="h-4 w-4" />
                        <span>{(t as any).settings?.caBundleUploaded || 'CA bundle uploaded'}</span>
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await apiClient.deleteClusterCaBundle(editingId);
                            toast.success('CA bundle removed');
                            fetchClusters();
                          } catch (err: any) {
                            toast.error(err?.message || 'Failed to remove CA bundle');
                          }
                        }}
                        className="text-sm text-red-500 hover:text-red-700 flex items-center space-x-1"
                      >
                        <X className="h-3 w-3" />
                        <span>{t.common.delete || 'Remove'}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 cursor-pointer">
                        <Upload className="h-4 w-4 mr-1.5" />
                        {(t as any).settings?.uploadCaBundle || 'Upload PEM'}
                        <input
                          type="file"
                          accept=".pem,.crt,.cer"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              await apiClient.uploadClusterCaBundle(editingId, file);
                              toast.success('CA bundle uploaded');
                              fetchClusters();
                            } catch (err: any) {
                              toast.error(err?.message || 'Failed to upload CA bundle');
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {(t as any).settings?.caBundleHint || 'For clusters with internal CA certificates'}
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
            <div className="flex space-x-3">
              <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md">
                {editingId ? (t.common.update || 'Update') : ((t as any).settings?.addCluster || 'Add Cluster')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md"
              >
                {t.common.cancel}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Cluster list */}
      <div className="space-y-3">
        {clusters.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Globe className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>{(t as any).settings?.noClusters || 'No clusters configured'}</p>
          </div>
        ) : (
          clusters.map((cluster) => (
            <div
              key={cluster.id}
              className={clsx(
                'bg-white dark:bg-gray-800 border rounded-lg p-4 transition-colors',
                cluster.is_active
                  ? 'border-blue-200 dark:border-blue-800'
                  : 'border-gray-200 dark:border-gray-700 opacity-60'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3">
                    {healthIcon(cluster.health_status)}
                    <h4 className="text-md font-medium text-gray-900 dark:text-white truncate">
                      {cluster.name}
                    </h4>
                    {cluster.is_base && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {t.settings.clusterBase}
                      </span>
                    )}
                    {!cluster.is_active && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                        {t.settings.inactive}
                      </span>
                    )}
                    {!cluster.verify_ssl && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                        {t.settings.sslOff}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                    {cluster.base_url}
                  </p>
                  {cluster.health_message && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {translateHealthMessage(cluster.health_message)}
                      {cluster.last_health_check && ` — ${new Date(cluster.last_health_check).toLocaleString()}`}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleTest(cluster.id)}
                    disabled={testingId === cluster.id}
                    className="px-3 py-1 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 flex items-center space-x-1"
                  >
                    {testingId === cluster.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    <span>{t.settings.clusterTest}</span>
                  </button>

                  <button
                    onClick={() => handleEditCluster(cluster)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>

                  {!cluster.is_base && (
                    <>
                      <button
                        onClick={() => handleToggleActive(cluster)}
                        className={clsx(
                          'px-3 py-1 text-sm font-medium rounded-md',
                          cluster.is_active
                            ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'
                        )}
                      >
                        {cluster.is_active ? t.settings.active : t.settings.inactive}
                      </button>
                      <button
                        onClick={() => handleDelete(cluster.id, cluster.name)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
